import { createSlice } from '@reduxjs/toolkit';

const TAG = '[calendarSharesSlice]';

const initialState = {
  // Shares where user is the owner (user's calendar shared with others)
  // Map: key = share id, value = { owner_id, shared_with_id, created_at }
  // null = not loaded yet, {} = loaded but empty
  ownedShares: null,
  // Shares where user is the recipient (others' calendars shared with user)
  // Map: key = share id, value = { owner_id, shared_with_id, created_at }
  // null = not loaded yet, {} = loaded but empty
  receivedShares: null,
  // Flag indicating if shares have been loaded from the database (even if empty)
  loaded: false,
  // Loading state for initial load
  loading: false,
};

const calendarSharesSlice = createSlice({
  name: 'calendarShares',
  initialState,
  reducers: {
    // Set the loaded flag to true (called after initial load completes)
    setSharesLoaded: (state) => {
      state.loaded = true;
    },
    
    // Set loading state
    setSharesLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    // Set owned shares (called on initial load - where user is owner)
    setOwnedShares: (state, action) => {
      const sharesArray = action.payload;
      const sharesMap = {};
      sharesArray.forEach((share) => {
        sharesMap[share.id] = share;
      });
      state.ownedShares = sharesMap;
    },

    // Set received shares (called on initial load - where user is recipient)
    setReceivedShares: (state, action) => {
      const sharesArray = action.payload;
      const sharesMap = {};
      sharesArray.forEach((share) => {
        sharesMap[share.id] = share;
      });
      state.receivedShares = sharesMap;
    },

    // Add or update an owned share (called by realtime INSERT/UPDATE on owner channel)
    upsertOwnedShare: (state, action) => {
      const share = action.payload;
      if (!state.ownedShares) {
        state.ownedShares = {};
      }
      state.ownedShares[share.id] = share;
    },

    // Add or update a received share (called by realtime INSERT/UPDATE on recipient channel)
    upsertReceivedShare: (state, action) => {
      const share = action.payload;
      if (!state.receivedShares) {
        state.receivedShares = {};
      }
      state.receivedShares[share.id] = share;
    },

    // Remove an owned share (called by realtime DELETE on owner channel)
    removeOwnedShare: (state, action) => {
      const share = action.payload;
      if (state.ownedShares) {
        delete state.ownedShares[share.id];
      }
    },

    // Remove a received share (called by realtime DELETE on recipient channel)
    removeReceivedShare: (state, action) => {
      const share = action.payload;
      if (state.receivedShares) {
        delete state.receivedShares[share.id];
      }
    },
    
    // Clear all shares (called on logout)
    clearShares: (state) => {
      state.ownedShares = {};
      state.receivedShares = {};
      state.loaded = false;
    },
  },
});

export const {
  setSharesLoaded,
  setSharesLoading,
  setOwnedShares,
  setReceivedShares,
  upsertOwnedShare,
  upsertReceivedShare,
  removeOwnedShare,
  removeReceivedShare,
  clearShares,
} = calendarSharesSlice.actions;

export default calendarSharesSlice.reducer;
